// src/api/checkoutApi.js
//
// Uses customer_orders_v1 — the only confirmed order GCF endpoint.
// Order creation vs. history retrieval is differentiated via `action` in the
// request body, following the same pattern as cart_operations (add/update/delete).
//
import { CUSTOMER_ORDERS_V1_URL } from "../config/apiConfig";
import { getAuthToken } from "../utils/auth";

function authHeaders() {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Place a new order via customer_orders_v1.
 *
 * Payload shape sent to the GCF:
 * {
 *   action: "create_order",
 *   customer_id,
 *   shipping_address, billing_address,
 *   payment_method,   card_last_four?,
 *   items: [{ article_id, name, quantity, price, size, image }],
 *   subtotal, tax, shipping, discount, total, promo_code?
 * }
 *
 * Expected GCF response: { order_id?, order_number?, status, message, ... }
 *
 * NOTE: If the GCF uses a different action key (e.g. "place_order"), update
 * the `action` field below — no other change is needed.
 */
export const placeOrder = async (orderPayload) => {
  const body = { action: "create_order", ...orderPayload };

  const response = await fetch(CUSTOMER_ORDERS_V1_URL, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `Order failed (${response.status})`);
  }

  return data;
};

/**
 * Fetch order history for a customer.
 *
 * Payload: { action: "get_orders", customer_id }
 * Returns an array of order objects.
 */
export const fetchOrderHistory = async (customerId) => {
  const response = await fetch(CUSTOMER_ORDERS_V1_URL, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ action: "get_orders", customer_id: customerId }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `Failed to fetch orders (${response.status})`);
  }

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.orders)) return data.orders;
  return [];
};
